import type { FC, ReactNode } from 'react'
import type { FooterProps } from '@/types'
import type { FooterNavLink } from '@/components/generals/footer-nav'

import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button-link'
import { FooterNav } from '@/components/generals/footer-nav'
import { SocialIcon } from '@/components/generals/social-icon'
import { CONTACT_HREF, PORTFOLIO_HREF, siteConfig } from '@/constants/site'

const EXPLORE_LINKS: Array<FooterNavLink> = [
    { key: 'home', href: '/', label: 'Home' },
    { key: 'services', href: '/services', label: 'Services' },
    { key: 'portfolio', href: PORTFOLIO_HREF, label: 'Portfolio' },
    { key: 'testimonials', href: '/#testimonials', label: 'Testimonials' }
]

const CONNECT_LINKS: Array<FooterNavLink> = [
    { key: 'email', href: CONTACT_HREF, label: 'Email Us' },
    { key: 'newsletter', href: '/services#newsletter', label: 'Newsletter' }
]

// Home page footer: brand message on the left, link columns on the right,
// copyright and social icons along the bottom.
export const FooterSplit: FC<FooterProps> = ({ year, socialLinks = [] }): ReactNode => {
    return (
        <footer className='w-full bg-background text-foreground'>
            <div className='px-4 md:px-8'>
                <div className='mx-auto w-full max-w-site py-12 md:py-16 lg:py-20'>
                    <div className='grid gap-12 lg:grid-cols-[0.4fr_1fr] lg:gap-0'>
                        <div className='flex flex-col items-start gap-4 lg:pr-8'>
                            <Logo size='lg' />

                            <p className='font-heading text-heading-6 font-normal text-balance'>
                                Thank you for visiting our site. We hope you found what you were looking for. Reach out to us with any questions or feedback!
                            </p>

                            <ButtonLink href={CONTACT_HREF}>
                                Get Started
                            </ButtonLink>
                        </div>

                        <div className='grid grid-cols-2 gap-x-4 gap-y-8 md:gap-x-8'>
                            <FooterNav title='Explore More' links={EXPLORE_LINKS} />
                            <FooterNav title='Stay Connected' links={CONNECT_LINKS} />
                        </div>
                    </div>

                    <div className='mt-12 flex flex-col items-start gap-5 md:mt-24 md:flex-row md:items-center md:justify-between'>
                        <p className='text-base opacity-50'>
                            {year ? `© Copyright ${year} ${siteConfig.name}` : `© Copyright ${siteConfig.name}`}
                        </p>

                        {socialLinks.length > 0 && (
                            <ul className='flex items-center gap-4' aria-label='Social media'>
                                {socialLinks.map(link => (
                                    <li key={link.id}>
                                        <a
                                            href={link.url}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={link.platform}
                                            className='flex text-foreground opacity-70 transition-opacity duration-300 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary'
                                        >
                                            <SocialIcon platform={link.platform} />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    )
}
